import { z } from 'zod';
import { doctorMeSchema, queueItemSchema, doctorAppointmentSchema, hostJoinTicketSchema, healthieHandoffSchema, huddleNoteSchema, rosterPatientSchema, patientBriefSchema, doctorChatThreadSchema, doctorAvailabilitySchema, availabilitySlotsSchema, doctorProfileSchema } from '@forcebase-ai/schema/doctor';
import { kpiSummarySchema, memberRowSchema, memberDetailSchema, doctorRowSchema, doctorDetailSchema, staffRowSchema, inviteStaffResultSchema, applicationRowSchema, visitRowSchema, visitHistoryRowSchema, paymentsViewSchema, emrSyncRowSchema, reconciliationRowSchema, auditRowSchema, commandSettingsSchema } from '@forcebase-ai/schema/command';
import { applicationResultSchema } from '@forcebase-ai/schema/public';
import { patientMeSchema, avatarResultSchema, chartLinkSchema, appointmentViewSchema, appointmentEventSchema, doctorSummarySchema, availabilitySchema, schedulingConfigSchema, joinTicketSchema, chatThreadSchema, visitSummaryViewSchema, billingSummarySchema, notificationPrefsSchema, walletPassResultSchema } from '@forcebase-ai/schema/patient';

// src/index.ts

// src/http.ts
var ApiError = class extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ApiError";
  }
  status;
  code;
};
var HttpClient = class {
  baseUrl;
  getToken;
  fetchImpl;
  constructor(opts) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.getToken = opts.getToken;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }
  async request(method, path, schema, body) {
    const headers = {};
    if (body !== void 0) headers["Content-Type"] = "application/json";
    const token = this.getToken ? await this.getToken() : null;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body === void 0 ? void 0 : JSON.stringify(body)
    });
    const text = await res.text();
    const json = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const err = json.error;
      throw new ApiError(res.status, err?.code ?? "error", err?.message ?? "Request failed");
    }
    const data = json.data;
    return schema.parse(data);
  }
  /** POST and consume a Server-Sent Events stream — for endpoints that stream (e.g. the concierge).
   * Auth is identical to request(). Yields each SSE frame as `{ event, data }`. A non-stream response
   * (e.g. a 429/401 JSON error) throws `ApiError` before any frame is yielded. */
  async *requestSSE(path, body) {
    const headers = {
      "Content-Type": "application/json",
      Accept: "text/event-stream"
    };
    const token = this.getToken ? await this.getToken() : null;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body)
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      let code = "error";
      let message = "Request failed";
      try {
        const err = JSON.parse(text).error;
        if (err) {
          code = err.code ?? code;
          message = err.message ?? message;
        }
      } catch {
      }
      throw new ApiError(res.status, code, message);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (; ; ) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let event = "message";
        let data = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) data += line.slice(5).trim();
        }
        yield { event, data };
      }
    }
  }
};
var idSchema = z.object({ id: z.string().uuid().optional() });
var okSchema = z.object({ ok: z.boolean() });
var huddleSavedSchema = z.object({
  id: z.string().uuid().optional(),
  body: z.string(),
  updated_at: z.string().optional()
});
var DoctorClient = class extends HttpClient {
  me() {
    return this.request("GET", "/v1/doctor/me", doctorMeSchema);
  }
  // --- Schedule / queue ----------------------------------------------------
  queue() {
    return this.request("GET", "/v1/doctor/queue", z.array(queueItemSchema));
  }
  appointment(id) {
    return this.request(
      "GET",
      `/v1/doctor/appointments/${id}`,
      doctorAppointmentSchema
    );
  }
  reschedule(id, body) {
    return this.request("PATCH", `/v1/doctor/appointments/${id}`, idSchema, body);
  }
  cancel(id, body) {
    return this.request("DELETE", `/v1/doctor/appointments/${id}`, idSchema, body);
  }
  // --- Visit (Zoom host + Healthie hand-off + huddle + summary) -------------
  joinVisitHost(id) {
    return this.request(
      "GET",
      `/v1/doctor/visits/${id}/join`,
      hostJoinTicketSchema
    );
  }
  /** Step-up-MFA-gated OIDC SSO hand-off — the ONLY path to clinical data (D2/D5/D10). */
  openChart(patientId) {
    return this.request(
      "POST",
      `/v1/doctor/patients/${patientId}/open-chart`,
      healthieHandoffSchema,
      {}
    );
  }
  /** Thin ≤280-char convenience note (NOT a diagnosis/Rx/lab — D5). */
  writeVisitSummary(patientId, body) {
    return this.request("POST", `/v1/doctor/patients/${patientId}/visit-summary`, okSchema, body);
  }
  huddle(appointmentId) {
    return this.request(
      "GET",
      `/v1/doctor/appointments/${appointmentId}/huddle`,
      huddleNoteSchema
    );
  }
  saveHuddle(appointmentId, body) {
    return this.request(
      "POST",
      `/v1/doctor/appointments/${appointmentId}/huddle`,
      huddleSavedSchema,
      { body }
    );
  }
  // --- Patients (roster + thin brief; chart is REPLACED) -------------------
  patients() {
    return this.request(
      "GET",
      "/v1/doctor/patients",
      z.array(rosterPatientSchema)
    );
  }
  patientBrief(id) {
    return this.request("GET", `/v1/doctor/patients/${id}/brief`, patientBriefSchema);
  }
  // --- Messages ------------------------------------------------------------
  messages(patientId) {
    return this.request(
      "GET",
      `/v1/doctor/patients/${patientId}/messages`,
      doctorChatThreadSchema
    );
  }
  sendMessage(patientId, body) {
    return this.request("POST", `/v1/doctor/patients/${patientId}/messages`, idSchema, { body });
  }
  // --- Availability --------------------------------------------------------
  availability() {
    return this.request(
      "GET",
      "/v1/doctor/availability",
      doctorAvailabilitySchema
    );
  }
  availabilitySlots(from, to) {
    const q = new URLSearchParams({ from, to });
    return this.request("GET", `/v1/doctor/availability/slots?${q}`, availabilitySlotsSchema);
  }
  addException(ex) {
    return this.request("POST", "/v1/doctor/availability/exceptions", idSchema, ex);
  }
  deleteException(exId) {
    return this.request("DELETE", `/v1/doctor/availability/exceptions/${exId}`, okSchema);
  }
  setWeekly(slots) {
    return this.request("PUT", "/v1/doctor/availability/weekly", okSchema, { slots });
  }
  // --- Profile / credentials ----------------------------------------------
  profile() {
    return this.request("GET", "/v1/doctor/profile", doctorProfileSchema);
  }
  saveProfile(body) {
    return this.request("PUT", "/v1/doctor/profile", okSchema, body);
  }
  // --- Push (mobile) -------------------------------------------------------
  /** Register an APNs/FCM device token for push. Opaque device id; never PHI. */
  registerPushToken(platform, token) {
    return this.request("POST", "/v1/doctor/push-tokens", okSchema, { platform, token });
  }
};
var okSchema2 = z.object({}).passthrough();
var CommandClient = class extends HttpClient {
  kpis() {
    return this.request("GET", "/v1/command/kpis", kpiSummarySchema);
  }
  // --- Members -------------------------------------------------------------
  members() {
    return this.request("GET", "/v1/command/members", z.array(memberRowSchema));
  }
  member(id) {
    return this.request("GET", `/v1/command/members/${id}`, memberDetailSchema);
  }
  // Member management writes (SOW §3.5). Each mutates the patients row, audits at PHI_ADMIN,
  // and enqueues a member.* notification server-side.
  changeMemberTier(id, tier) {
    return this.request("POST", `/v1/command/members/${id}/tier`, okSchema2, { tier });
  }
  changeMemberStatus(id, status) {
    return this.request("POST", `/v1/command/members/${id}/status`, okSchema2, { status });
  }
  editMember(id, fields) {
    return this.request("POST", `/v1/command/members/${id}/edit`, okSchema2, fields);
  }
  // --- Doctors -------------------------------------------------------------
  doctors() {
    return this.request("GET", "/v1/command/doctors", z.array(doctorRowSchema));
  }
  doctor(id) {
    return this.request("GET", `/v1/command/doctors/${id}`, doctorDetailSchema);
  }
  // Doctor management writes (SOW §3.5). Privileged-pool writes; audited NONE server-side.
  // editDoctor: profile and/or APPEND-ONLY credential version. deactivate is guarded against doctors
  // with upcoming visits (409). resyncDoctorEmr re-provisions the Healthie provider.
  editDoctor(id, body) {
    return this.request("POST", `/v1/command/doctors/${id}/edit`, okSchema2, body);
  }
  deactivateDoctor(id) {
    return this.request("POST", `/v1/command/doctors/${id}/deactivate`, okSchema2, {});
  }
  reactivateDoctor(id) {
    return this.request("POST", `/v1/command/doctors/${id}/reactivate`, okSchema2, {});
  }
  resyncDoctorEmr(id) {
    return this.request("POST", `/v1/command/doctors/${id}/resync`, okSchema2, {});
  }
  // --- Staff (create doctor/command accounts) ------------------------------
  listStaff(role) {
    return this.request(
      "GET",
      `/v1/command/staff?role=${role}`,
      z.array(staffRowSchema)
    );
  }
  inviteStaff(input) {
    return this.request("POST", "/v1/command/staff", inviteStaffResultSchema, input);
  }
  // --- Applications --------------------------------------------------------
  applications() {
    return this.request(
      "GET",
      "/v1/command/applications",
      z.array(applicationRowSchema)
    );
  }
  approveApplication(id) {
    return this.request("POST", `/v1/command/applications/${id}/approve`, okSchema2, {});
  }
  rejectApplication(id) {
    return this.request("POST", `/v1/command/applications/${id}/reject`, okSchema2, {});
  }
  // --- Visits oversight ----------------------------------------------------
  visits() {
    return this.request("GET", "/v1/command/visits", z.array(visitRowSchema));
  }
  visitHistory(id) {
    return this.request(
      "GET",
      `/v1/command/visits/${id}/history`,
      z.array(visitHistoryRowSchema)
    );
  }
  // --- Payments (membership only) -----------------------------------------
  payments() {
    return this.request("GET", "/v1/command/payments", paymentsViewSchema);
  }
  // --- EMR Sync: failed (+retry) and reconciliation drift (+resolve) -------
  emrSync() {
    return this.request("GET", "/v1/command/emr-sync", z.array(emrSyncRowSchema));
  }
  retrySync(id) {
    return this.request("POST", `/v1/command/emr-sync/${id}/retry`, okSchema2, {});
  }
  reconciliation() {
    return this.request(
      "GET",
      "/v1/command/reconciliation",
      z.array(reconciliationRowSchema)
    );
  }
  // True-resolve: a real repair with an explicit fork — accept_healthie (adopt Healthie)
  // vs push_cc (re-mirror CC → Healthie). `action` is required.
  resolveDrift(id, action) {
    return this.request("POST", `/v1/command/reconciliation/${id}/resolve`, okSchema2, { action });
  }
  // --- Compliance / Audit (metadata only) ----------------------------------
  audit(limit = 100) {
    return this.request(
      "GET",
      `/v1/command/audit?limit=${limit}`,
      z.array(auditRowSchema)
    );
  }
  // --- Settings ------------------------------------------------------------
  settings() {
    return this.request("GET", "/v1/command/settings", commandSettingsSchema);
  }
};
var PublicClient = class extends HttpClient {
  /** Submit a doctor or fighter application. Returns the created (or existing, when deduped) row. */
  submitApplication(body) {
    return this.request(
      "POST",
      "/v1/public/applications",
      applicationResultSchema,
      body
    );
  }
};
var idSchema2 = z.object({ id: z.string().uuid().optional() });
var urlSchema = z.object({ url: z.string() });
var okSchema3 = z.object({ ok: z.boolean() });
var PatientClient = class extends HttpClient {
  constructor(opts) {
    super(opts);
  }
  /** Register an APNs/FCM device token for push (mobile). Opaque device id; never PHI. */
  registerPushToken(platform, token) {
    return this.request("POST", "/v1/patient/push-tokens", okSchema3, { platform, token });
  }
  // --- AI Concierge (Aria) — streaming -------------------------------------
  /** Stream Aria's reply token-by-token. Yields `chunk` events to append as they arrive; a `refusal`
   * or `error` event means discard the partial and show that text. Throws `ApiError` on a non-stream
   * failure (e.g. 429 when over the rate limit). Bodies are never persisted — pass conversation
   * turns each call (the API is stateless). */
  async *conciergeStream(messages) {
    for await (const frame of this.requestSSE("/v1/patient/concierge", { messages })) {
      if (frame.event === "done") return;
      const text = JSON.parse(frame.data).text ?? "";
      if (frame.event === "chunk") yield { type: "chunk", text };
      else if (frame.event === "refusal") yield { type: "refusal", text };
      else if (frame.event === "error") yield { type: "error", text };
    }
  }
  // --- Profile -------------------------------------------------------------
  me() {
    return this.request("GET", "/v1/patient/me", patientMeSchema);
  }
  /** Upload a profile photo as a png/jpeg/webp data: URL (resize client-side first). */
  uploadAvatar(image) {
    return this.request("POST", "/v1/patient/avatar", avatarResultSchema, { image });
  }
  // --- Secure chart (member-facing Healthie hand-off — the record lives in Healthie, D5) --------
  openChart() {
    return this.request("POST", "/v1/patient/chart-link", chartLinkSchema, {});
  }
  // --- Appointments --------------------------------------------------------
  listAppointments() {
    return this.request(
      "GET",
      "/v1/patient/appointments",
      z.array(appointmentViewSchema)
    );
  }
  appointmentEvents(id) {
    return this.request(
      "GET",
      `/v1/patient/appointments/${id}/events`,
      z.array(appointmentEventSchema)
    );
  }
  book(req) {
    return this.request("POST", "/v1/patient/appointments", idSchema2, req);
  }
  reschedule(id, body) {
    return this.request("PATCH", `/v1/patient/appointments/${id}`, idSchema2, body);
  }
  cancel(id, body) {
    return this.request("DELETE", `/v1/patient/appointments/${id}`, idSchema2, body);
  }
  // --- Booking inputs ------------------------------------------------------
  listDoctors() {
    return this.request(
      "GET",
      "/v1/patient/doctors",
      z.array(doctorSummarySchema)
    );
  }
  availability(doctorId, from, to) {
    const q = new URLSearchParams({ doctor_id: doctorId, from, to });
    return this.request("GET", `/v1/patient/availability?${q}`, availabilitySchema);
  }
  schedulingConfig() {
    return this.request(
      "GET",
      "/v1/patient/scheduling-config",
      schedulingConfigSchema
    );
  }
  // --- Visit join (Zoom Meeting SDK) --------------------------------------
  joinVisit(id) {
    return this.request("GET", `/v1/patient/visits/${id}/join`, joinTicketSchema);
  }
  // --- Chat ----------------------------------------------------------------
  messages() {
    return this.request("GET", "/v1/patient/messages", chatThreadSchema);
  }
  sendMessage(body) {
    return this.request("POST", "/v1/patient/messages", idSchema2, { body });
  }
  // --- Summary -------------------------------------------------------------
  visitSummaries() {
    return this.request(
      "GET",
      "/v1/patient/visit-summaries",
      z.array(visitSummaryViewSchema)
    );
  }
  // --- Billing -------------------------------------------------------------
  billing() {
    return this.request("GET", "/v1/patient/billing", billingSummarySchema);
  }
  billingCheckout(tier) {
    return this.request("POST", "/v1/patient/billing/checkout", urlSchema, { tier });
  }
  billingPortal() {
    return this.request("POST", "/v1/patient/billing/portal", urlSchema, {});
  }
  // --- Settings ------------------------------------------------------------
  notificationPrefs() {
    return this.request(
      "GET",
      "/v1/patient/notification-preferences",
      notificationPrefsSchema
    );
  }
  updateNotificationPrefs(prefs) {
    return this.request(
      "PUT",
      "/v1/patient/notification-preferences",
      notificationPrefsSchema,
      prefs
    );
  }
  // --- Wallet (mobile) — membership pass; identifiers only, no clinical data --------------------
  walletPass(provider) {
    return this.request(
      "POST",
      "/v1/patient/wallet-pass",
      walletPassResultSchema,
      { provider }
    );
  }
};

export { ApiError, CommandClient, DoctorClient, PatientClient, PublicClient };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map